import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import Icon from './Icon';
import GreenToast from '../icons/GreenToast.svg';
import BlueToast from '../icons/BlueToast.svg';
import YellowToast from '../icons/YellowToast.svg';
import RedToast from '../icons/RedToast.svg';
import '../styles/Modal.css';

const TOAST_ICONS = {
  success: GreenToast,
  info: BlueToast,
  warning: YellowToast,
  error: RedToast
};

/**
 * Unified Modal Component
 *
 * Types:
 * - toast: Auto-dismissing notification (success, error, warning, info)
 * - confirm: Confirmation dialog with yes/no buttons
 * - alert: Simple alert with OK button
 * - custom: Custom modal with your own content
 *
 * Usage Examples:
 *
 * Toast:
 * modal.toast('Success!', 'success')
 * modal.toast('Error occurred', 'error')
 *
 * Confirm:
 * const result = await modal.confirm('Delete this item?', 'Are you sure?')
 * if (result) { // user clicked yes }
 *
 * Alert:
 * modal.alert('Please select a brand first')
 *
 * Custom:
 * modal.custom({ title: 'Edit Item', content: <YourComponent />, onConfirm: () => {} })
 */

const Modal = ({
  type = 'custom', // 'toast', 'confirm', 'alert', 'custom'
  variant = 'info', // For toast: 'success', 'error', 'warning', 'info'
  title,
  message,
  content,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  onClose,
  autoClose = true, // For toast only
  duration = 4000, // For toast only (ms)
  showCloseButton = true,
  size = 'medium' // 'small', 'medium', 'large'
}) => {
  useEffect(() => {
    // Auto-close for toast notifications
    if (type === 'toast' && autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, autoClose, duration]);

  const handleClose = () => {
    if (onClose) onClose();
    if (onCancel) onCancel();
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    if (onClose) onClose();
  };

  // Toast notifications render differently
  if (type === 'toast') {
    return ReactDOM.createPortal(
      <div className={`modal-toast modal-toast-${variant}`}>
        <div className="modal-toast-icon">
          <img src={TOAST_ICONS[variant]} alt="" />
        </div>
        <div className="modal-toast-content">
          {title && <div className="modal-toast-title">{title}</div>}
          <div className="modal-toast-message">{message}</div>
        </div>
        {showCloseButton && (
          <button className="modal-toast-close" onClick={handleClose}>
            <Icon name="close-x" />
          </button>
        )}
      </div>,
      document.body
    );
  }

  // Regular modals (confirm, alert, custom)
  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className={`modal-container modal-${size}`}>
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && <h3 className="modal-title">{title}</h3>}
            {showCloseButton && (
              <button className="modal-close-btn" onClick={handleClose} aria-label="Close" />
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {message && <p className="modal-message">{message}</p>}
          {content && <div className="modal-custom-content">{content}</div>}
        </div>

        {/* Footer with actions */}
        {(type === 'confirm' || type === 'alert' || onConfirm || onCancel) && (
          <div className="modal-footer">
            {type === 'confirm' && (
              <>
                <button className="modal-btn modal-btn-cancel" onClick={handleClose}>
                  {cancelText}
                </button>
                <button className="modal-btn modal-btn-confirm" onClick={handleConfirm}>
                  {confirmText}
                </button>
              </>
            )}
            {type === 'alert' && (
              <button className="modal-btn modal-btn-confirm" onClick={handleClose}>
                OK
              </button>
            )}
            {type === 'custom' && (
              <>
                {onCancel && (
                  <button className="modal-btn modal-btn-cancel" onClick={handleClose}>
                    {cancelText}
                  </button>
                )}
                {onConfirm && (
                  <button className="modal-btn modal-btn-confirm" onClick={handleConfirm}>
                    {confirmText}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
